// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { useCallback, useEffect, useMemo, useState } from "react";

import { CircleAlert, CircleCheck, RefreshCw } from "lucide-react";

import type {
  CockpitApi,
  EvaluationCorpus,
  EvaluationMetrics,
  EvaluationRun,
} from "./api.js";
import { displayState, stateTone } from "./presentation.js";

interface EvaluationState {
  readonly corpora: readonly EvaluationCorpus[];
  readonly runs: readonly EvaluationRun[];
}

const EMPTY_STATE: EvaluationState = { corpora: [], runs: [] };
const CORPUS_TEMPLATE = JSON.stringify(
  {
    cases: [],
    corpusId: "corpus:held-out-v1",
    description: "Frozen, licensed, human-labeled held-out corpus.",
    partition: "held-out",
    visibility: "public",
  },
  null,
  2,
);
const RUN_TEMPLATE = JSON.stringify(
  {
    corpusDigest: "replace-with-selected-corpus-digest",
    corpusId: "corpus:held-out-v1",
    mode: "deterministic",
    reproducibility: {
      environmentDigest: "0".repeat(64),
      harnessDigest: "0".repeat(64),
      model: {
        adapterId: "model-adapter",
        modelId: "model",
        revision: "immutable-revision",
      },
      orchestrationDigest: "0".repeat(64),
      promptDigest: "0".repeat(64),
      protocolDigests: ["0".repeat(64)],
      runtimeVersion: "diffclaw-evaluation-runner-v1",
      toolRegistryDigest: "0".repeat(64),
    },
    runId: "run:candidate-v1",
    samplesPerCase: 1,
    target: {
      digest: "0".repeat(64),
      identity: "candidate",
      kind: "orchestration",
      revision: "immutable-revision",
    },
  },
  null,
  2,
);
const THRESHOLD_TEMPLATE = JSON.stringify(
  [
    { comparator: "minimum", metric: "precision", value: 0.9 },
    {
      comparator: "minimum",
      metric: "severityWeightedRecall",
      value: 0.9,
    },
    { comparator: "minimum", metric: "chronicle.privacy", value: 1 },
  ],
  null,
  2,
);

function jsonObject(
  value: string,
  name: string,
): Readonly<Record<string, unknown>> {
  const parsed: unknown = JSON.parse(value);
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new TypeError(`${name} must be a JSON object.`);
  }
  return parsed as Readonly<Record<string, unknown>>;
}

function jsonArray(value: string, name: string): readonly unknown[] {
  const parsed: unknown = JSON.parse(value);
  if (!Array.isArray(parsed)) {
    throw new TypeError(`${name} must be a JSON array.`);
  }
  return parsed;
}

function metricRows(
  candidate: EvaluationMetrics | null,
  baseline: EvaluationMetrics | null,
): readonly {
  readonly baseline: number | null;
  readonly candidate: number | null;
  readonly label: string;
}[] {
  return [
    {
      baseline: baseline?.precision ?? null,
      candidate: candidate?.precision ?? null,
      label: "Precision",
    },
    {
      baseline: baseline?.severityWeightedRecall ?? null,
      candidate: candidate?.severityWeightedRecall ?? null,
      label: "Severity-weighted recall",
    },
    {
      baseline: baseline?.evidenceValidity ?? null,
      candidate: candidate?.evidenceValidity ?? null,
      label: "Evidence validity",
    },
    {
      baseline: baseline?.locationAccuracy ?? null,
      candidate: candidate?.locationAccuracy ?? null,
      label: "Location accuracy",
    },
    {
      baseline: baseline?.chronicle.privacy ?? null,
      candidate: candidate?.chronicle.privacy ?? null,
      label: "Chronicle privacy",
    },
    {
      baseline: baseline?.latencyMillisP95 ?? null,
      candidate: candidate?.latencyMillisP95 ?? null,
      label: "Latency p95 (ms)",
    },
    {
      baseline: baseline?.costUsdTotal ?? null,
      candidate: candidate?.costUsdTotal ?? null,
      label: "Cost (USD)",
    },
  ];
}

function number(value: number | null): string {
  return value === null ? "not measured" : value.toFixed(value >= 10 ? 0 : 3);
}

export function Evaluation({ api }: { readonly api: CockpitApi }) {
  const [state, setState] = useState<EvaluationState>(EMPTY_STATE);
  const [status, setStatus] = useState("Loading evaluation evidence…");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [corpusDefinition, setCorpusDefinition] = useState(CORPUS_TEMPLATE);
  const [runDefinition, setRunDefinition] = useState(RUN_TEMPLATE);
  const [candidateRunId, setCandidateRunId] = useState("");
  const [baselineRunId, setBaselineRunId] = useState("");
  const [promotionId, setPromotionId] = useState("");
  const [decision, setDecision] = useState<"accepted" | "rejected" | "shadow">(
    "shadow",
  );
  const [rationale, setRationale] = useState("");
  const [thresholds, setThresholds] = useState(THRESHOLD_TEMPLATE);

  const reload = useCallback(async () => {
    setStatus("Loading evaluation evidence…");
    setLoadError(null);
    try {
      const [corpora, runs] = await Promise.all([
        api.evaluationCorpora(),
        api.evaluationRuns(),
      ]);
      setState({ corpora, runs });
      setCandidateRunId((current) =>
        current.length > 0 ? current : (runs.at(-1)?.runId ?? ""),
      );
      setStatus("");
    } catch (error) {
      setStatus("");
      setLoadError(error instanceof Error ? error.message : "Loading failed.");
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const candidate = state.runs.find((run) => run.runId === candidateRunId);
  const baseline = state.runs.find((run) => run.runId === baselineRunId);
  const comparison = useMemo(
    () => metricRows(candidate?.metrics ?? null, baseline?.metrics ?? null),
    [baseline, candidate],
  );

  async function perform(label: string, operation: () => Promise<unknown>) {
    setStatus(label);
    try {
      await operation();
      await reload();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : `${label} failed.`);
    }
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Evaluation</h1>
          <p>
            Compare a candidate against the current default on frozen, licensed
            cases. Cost, latency, and uncertainty are all kept, and no candidate
            becomes the default without a person deciding it should.
          </p>
        </div>
        <div className="page-head-actions">
          <button
            className="button secondary"
            onClick={() => void reload()}
            type="button"
          >
            <RefreshCw size={16} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </header>

      {status.length === 0 ? null : (
        <p aria-live="polite" className="status-line">
          {status}
        </p>
      )}

      {loadError === null ? null : (
        <div className="callout warning" role="status" title={loadError}>
          <CircleAlert size={19} aria-hidden="true" />
          <div>
            <strong>Evaluation evidence is not available here</strong>
            <p>
              Review work is unaffected. This page fills in once the evaluation
              service is reachable.
            </p>
          </div>
        </div>
      )}

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Test sets</h2>
            <p>
              Frozen, human-labeled examples. Because they never change, two
              candidates can be compared on identical ground.
            </p>
          </div>
          <span className="count-badge">{state.corpora.length}</span>
        </div>
        {state.corpora.length === 0 ? (
          <div className="empty-state inline">
            <strong>No test set registered</strong>
            <span>
              Register a licensed, human-labeled set below to start comparing
              candidates.
            </span>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Corpus</th>
                  <th>Partition</th>
                  <th>Cases</th>
                  <th>Digest</th>
                </tr>
              </thead>
              <tbody>
                {state.corpora.map((corpus) => (
                  <tr key={corpus.corpusId}>
                    <th>
                      {corpus.description}
                      <small>{corpus.corpusId}</small>
                    </th>
                    <td>{corpus.partition}</td>
                    <td>{corpus.cases.length}</td>
                    <td>
                      <code>{corpus.contentDigest.slice(0, 12)}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <details className="disclosure">
          <summary>Register a test set</summary>
          <label>
            Corpus command
            <textarea
              className="code-input"
              onChange={(event) => {
                setCorpusDefinition(event.target.value);
              }}
              rows={14}
              spellCheck={false}
              value={corpusDefinition}
            />
          </label>
          <button
            className="button primary"
            onClick={() =>
              void perform("Registering frozen corpus…", () =>
                api.registerEvaluationCorpus(
                  jsonObject(corpusDefinition, "Corpus command"),
                ),
              )
            }
            type="button"
          >
            Register corpus
          </button>
        </details>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Runs</h2>
            <p>
              Each run is pinned to an exact version and keeps its uncertainty,
              latency, cost, and any partial results.
            </p>
          </div>
          <span className="count-badge">{state.runs.length}</span>
        </div>
        {state.runs.length === 0 ? (
          <div className="empty-state inline">
            <strong>No runs yet</strong>
            <span>
              Start a run against a registered test set to produce comparable
              evidence.
            </span>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Target</th>
                  <th>Progress</th>
                  <th>State</th>
                  <th>Control</th>
                </tr>
              </thead>
              <tbody>
                {state.runs.map((run) => (
                  <tr key={run.runId}>
                    <th>
                      {run.runId}
                      <small>
                        {run.resultDigest?.slice(0, 12) ?? "No result digest"}
                      </small>
                    </th>
                    <td>
                      {run.target.identity}
                      <small>{run.target.revision}</small>
                    </td>
                    <td>
                      {run.observations.length}/{run.expectedSampleCount}
                    </td>
                    <td>
                      <span
                        className={`state-badge tone-${stateTone(run.state)}`}
                      >
                        {displayState(run.state)}
                      </span>
                    </td>
                    <td>
                      <div className="button-row compact">
                        {run.state === "running" ? (
                          <>
                            <button
                              className="button secondary"
                              onClick={() =>
                                void perform("Cancelling evaluation…", () =>
                                  api.cancelEvaluationRun(
                                    run.runId,
                                    run.revision,
                                    "cockpit-operator-cancelled",
                                  ),
                                )
                              }
                              type="button"
                            >
                              Cancel
                            </button>
                            <button
                              className="button primary"
                              disabled={run.observations.length === 0}
                              onClick={() =>
                                void perform("Finalizing evaluation…", () =>
                                  api.finalizeEvaluationRun(
                                    run.runId,
                                    run.revision,
                                  ),
                                )
                              }
                              type="button"
                            >
                              Finalize
                            </button>
                          </>
                        ) : run.state === "cancelled" ||
                          run.state === "partial" ? (
                          <button
                            className="button secondary"
                            onClick={() =>
                              void perform("Resuming evaluation…", () =>
                                api.resumeEvaluationRun(
                                  run.runId,
                                  run.revision,
                                  "cockpit-operator-resumed",
                                ),
                              )
                            }
                            type="button"
                          >
                            Resume
                          </button>
                        ) : (
                          <span>Terminal</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <details className="disclosure">
          <summary>Start a run</summary>
          <label>
            Run command
            <textarea
              className="code-input"
              onChange={(event) => {
                setRunDefinition(event.target.value);
              }}
              rows={18}
              spellCheck={false}
              value={runDefinition}
            />
          </label>
          <button
            className="button primary"
            onClick={() =>
              void perform("Starting evaluation…", () =>
                api.startEvaluationRun(
                  jsonObject(runDefinition, "Run command"),
                ),
              )
            }
            type="button"
          >
            Start run
          </button>
        </details>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Candidate against baseline</h2>
            <p>
              Pick two completed runs. What you get is the evidence behind the
              difference, not a single headline score.
            </p>
          </div>
        </div>
        <div className="panel-pad form-grid">
          <label>
            Candidate
            <select
              onChange={(event) => {
                setCandidateRunId(event.target.value);
              }}
              value={candidateRunId}
            >
              <option value="">Select candidate</option>
              {state.runs.map((run) => (
                <option key={run.runId} value={run.runId}>
                  {run.runId} · {run.state}
                </option>
              ))}
            </select>
          </label>
          <label>
            Baseline
            <select
              onChange={(event) => {
                setBaselineRunId(event.target.value);
              }}
              value={baselineRunId}
            >
              <option value="">No baseline</option>
              {state.runs.map((run) => (
                <option key={run.runId} value={run.runId}>
                  {run.runId} · {run.state}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Baseline</th>
                <th>Candidate</th>
                <th>Delta</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((row) => (
                <tr key={row.label}>
                  <th>{row.label}</th>
                  <td>{number(row.baseline)}</td>
                  <td>{number(row.candidate)}</td>
                  <td>
                    {row.baseline === null || row.candidate === null
                      ? "not comparable"
                      : number(row.candidate - row.baseline)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Record the decision</h2>
            <p>
              Thresholds show you where a candidate falls short. Accepting it
              anyway is a decision only a person can record.
            </p>
          </div>
        </div>
        <div className="panel-pad form-grid">
          <label>
            Promotion ID
            <input
              onChange={(event) => {
                setPromotionId(event.target.value);
              }}
              placeholder="promotion:beta-2026-07"
              value={promotionId}
            />
          </label>
          <label>
            Decision
            <select
              onChange={(event) => {
                setDecision(
                  event.target.value as "accepted" | "rejected" | "shadow",
                );
              }}
              value={decision}
            >
              <option value="shadow">Shadow</option>
              <option value="accepted">Accept</option>
              <option value="rejected">Reject</option>
            </select>
          </label>
          <label className="wide">
            Rationale
            <textarea
              onChange={(event) => {
                setRationale(event.target.value);
              }}
              rows={3}
              value={rationale}
            />
          </label>
          <label className="wide">
            Predeclared thresholds
            <textarea
              className="code-input"
              onChange={(event) => {
                setThresholds(event.target.value);
              }}
              rows={10}
              spellCheck={false}
              value={thresholds}
            />
          </label>
        </div>
        <button
          className="button primary"
          disabled={
            candidateRunId.length === 0 ||
            promotionId.length === 0 ||
            rationale.trim().length === 0
          }
          onClick={() =>
            void perform("Recording human promotion decision…", () =>
              api.decideEvaluationPromotion({
                baselineRunId:
                  baselineRunId.length === 0 ? null : baselineRunId,
                decision,
                promotionId,
                rationale,
                runId: candidateRunId,
                thresholds: jsonArray(thresholds, "Thresholds"),
              }),
            )
          }
          type="button"
        >
          <CircleCheck size={17} aria-hidden="true" />
          Record decision
        </button>
      </section>
    </section>
  );
}
